
import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import { AdminDeleteDialogProps } from './types';

const AdminDeleteDialog = ({ admin, open, onOpenChange, onDelete }: AdminDeleteDialogProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle delete confirmation with improved UX and no redundant toasts
  const handleDeleteConfirm = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      console.log(`Attempting to delete admin: ${admin.firstName} ${admin.lastName} (ID: ${admin.id})`);
      
      const success = await onDelete(admin.id.toString());
      
      if (success) {
        console.log('Admin deletion successful');
        toast.success(`${admin.firstName} ${admin.lastName} has been deleted successfully`);
        onOpenChange(false);
        // No page reload - let React state management handle the refresh
      } else {
        console.error('Admin deletion failed - no success response');
        toast.error('Failed to delete administrator. Please try again.');
      }
    } catch (error) {
      console.error('Error during admin deletion:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('not found')) {
          toast.error('Administrator not found. The record may have already been deleted.');
          onOpenChange(false);
        } else if (error.message.includes('quota') || error.message.includes('storage')) {
          toast.error('Storage error occurred. Please try enabling desktop file storage.');
        } else {
          toast.error(`Failed to delete administrator: ${error.message}`);
        }
      } else {
        toast.error('An unexpected error occurred while deleting administrator');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Administrator</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete {admin.firstName} {admin.lastName}? This action cannot be undone.
            {admin.id && (
              <div className="mt-2 text-xs text-muted-foreground">
                Admin ID: {admin.id}
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default AdminDeleteDialog;
